import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { saveAs } from 'file-saver-es';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { ISupplierResponse } from '../../models/suppliers/supplier.model';
import { SupplierApiService } from '../../services/supplier-api.service';
import {
  CreateSupplier,
  DeleteSupplier,
  ExportProducts,
  ImportProducts,
  ImportSuppliers,
  LoadAllSuppliers,
  LoadSupplierById,
  LoadSuppliers,
  UpdateSupplier,
  ClearCurrentSupplier,
  FavoriteSupplier,
  UnfavoriteSupplier,
} from './supplier.actions';

export interface SupplierStateModel {
  suppliersLoaded: boolean;
  suppliers: ISupplierResponse[];
  allSuppliers: ISupplierResponse[];
  suppliersTotalPages: number;
  currentSupplier: ISupplierResponse;
}

const SUPPLIER_STATE_TOKEN = new StateToken<SupplierStateModel>('suppliers');

@State<SupplierStateModel>({
  name: SUPPLIER_STATE_TOKEN,
  defaults: {
    suppliersLoaded: false,
    suppliers: [],
    allSuppliers: [],
    suppliersTotalPages: 0,
    currentSupplier: <ISupplierResponse>{},
  },
})
@Injectable()
export class SupplierState {
  constructor(private service: SupplierApiService) {}

  @Action(LoadSuppliers, { cancelUncompleted: true })
  loadSuppliers(
    { getState, setState }: StateContext<SupplierStateModel>,
    { filter }: LoadSuppliers
  ) {
    return this.service.getPagedSuppliers(filter).pipe(
      tap((response: ApiResponse<ISupplierResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          suppliersLoaded: true,
          suppliers: response.data,
          suppliersTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllSuppliers, { cancelUncompleted: true })
  loadAllSuppliers(ctx: StateContext<SupplierStateModel>, { departmentId }: LoadAllSuppliers) {
    return this.service.getAllSuppliers(departmentId).pipe(
      tap((allSuppliers: ISupplierResponse[]) => {
        ctx.setState(patch({ allSuppliers }));
      })
    );
  }

  @Action(LoadSupplierById, { cancelUncompleted: true })
  loadSupplierById(
    { getState, setState }: StateContext<SupplierStateModel>,
    { id, departmentId, toCurrencyCode }: LoadSupplierById
  ) {
    return this.service.getSupplierById(id, departmentId, toCurrencyCode).pipe(
      tap((response: ISupplierResponse) => {
        const state = getState();
        setState({
          ...state,
          suppliersLoaded: true,
          currentSupplier: response,
        });
      })
    );
  }

  @Action(ImportProducts, { cancelUncompleted: true })
  importProducts(ctx: StateContext<SupplierStateModel>, { supplierId, file }: ImportProducts) {
    return this.service.importProducts(supplierId, file);
  }

  @Action(ImportSuppliers, { cancelUncompleted: true })
  @OnSuccess({ message: 'suppliersAreBeingImportedPleaseWait', dispatch: LoadSuppliers })
  importSuppliers(
    ctx: StateContext<SupplierStateModel>,
    { file, departments, departmentGroups }: ImportSuppliers
  ) {
    return this.service.importSuppliers(file, departments, departmentGroups);
  }

  @Action(ExportProducts, { cancelUncompleted: true })
  exportProducts(ctx: StateContext<SupplierStateModel>, { supplierId }: ExportProducts) {
    return this.service.exportProducts(supplierId).pipe(
      tap((exportedProducts: Blob) => {
        saveAs(exportedProducts, 'Export.xlsx');
      })
    );
  }

  @Action(CreateSupplier, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.Suppliers, message: 'supplierCreated' })
  createSupplier(ctx: StateContext<SupplierStateModel>, { supplier }: CreateSupplier) {
    return this.service.createSupplier(supplier);
  }

  @Action(UpdateSupplier, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.Suppliers, message: 'supplierUpdated' })
  updateSupplier(ctx: StateContext<SupplierStateModel>, { supplier }: UpdateSupplier) {
    return this.service.updateSupplier(supplier);
  }

  @Action(DeleteSupplier, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.Suppliers,
    message: 'supplierDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteSupplier(ctx: StateContext<SupplierStateModel>, { supplierId }: DeleteSupplier) {
    return this.service.deleteSupplier(supplierId);
  }

  @Action(ClearCurrentSupplier)
  clearCurrentSupplier(ctx: StateContext<SupplierStateModel>) {
    ctx.setState(patch({ currentSupplier: <ISupplierResponse>{} }));
  }

  @Action(FavoriteSupplier, { cancelUncompleted: true })
  @OnSuccess({
    message: 'supplierFavorited',
  })
  favoriteSupplier(ctx: StateContext<SupplierStateModel>, { supplierId }: FavoriteSupplier) {
    return this.service.favoriteSupplier(supplierId).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedSuppliers = state.suppliers.map((supplier) =>
          supplier.id === supplierId ? { ...supplier, isFavorite: true } : supplier
        );
        const updatedAllSuppliers = state.allSuppliers.map((supplier) =>
          supplier.id === supplierId ? { ...supplier, isFavorite: true } : supplier
        );
        ctx.setState(
          patch({
            suppliers: updatedSuppliers,
            allSuppliers: updatedAllSuppliers,
          })
        );
      })
    );
  }

  @Action(UnfavoriteSupplier)
  @OnSuccess({
    message: 'supplierUnfavorited',
  })
  unfavoriteSupplier(ctx: StateContext<SupplierStateModel>, { supplierId }: UnfavoriteSupplier) {
    return this.service.unfavoriteSupplier(supplierId).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedSuppliers = state.suppliers.map((supplier) =>
          supplier.id === supplierId ? { ...supplier, isFavorite: false } : supplier
        );
        const updatedAllSuppliers = state.allSuppliers.map((supplier) =>
          supplier.id === supplierId ? { ...supplier, isFavorite: false } : supplier
        );
        ctx.setState(
          patch({
            suppliers: updatedSuppliers,
            allSuppliers: updatedAllSuppliers,
          })
        );
      })
    );
  }

  @Selector()
  static suppliersLoaded(state: SupplierStateModel) {
    return state.suppliersLoaded;
  }

  @Selector()
  static getSuppliersTotalPages(state: SupplierStateModel) {
    return state.suppliersTotalPages;
  }

  @Selector()
  static getPagedSuppliers(state: SupplierStateModel) {
    return state.suppliers;
  }

  @Selector()
  static getAllSuppliers(state: SupplierStateModel) {
    return state.allSuppliers;
  }

  @Selector()
  static getSupplierById(state: any) {
    return state.currentSupplier;
  }
}
